import { useSession, useSignIn } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';

export const SSO_TICKET_PARAM = 'sso_ticket';

/**
 * Consumes the single-use Clerk sign-in ticket the backend appends when it
 * redirects an already-authenticated user to the studio (`?sso_ticket=...`).
 * The studio runs on a different origin than the backend, so it cannot see
 * the backend's Clerk session — the ticket lets it establish its own.
 *
 * Must settle before the auth gate decides an apparently signed-out user
 * needs redirecting — EditorEntry holds rendering on `isTicketPending`.
 */
export function useTicketSignIn() {
  const { signIn, setActive, isLoaded: isSignInLoaded } = useSignIn();
  const { session, isLoaded: isSessionLoaded } = useSession();
  const startedRef = useRef(false);
  // `get(...)` (not `has`) so a valueless `?sso_ticket=` never arms the
  // pending state — the consume effect ignores empty tickets, and a mismatch
  // here would leave the "Signing you in..." screen up forever.
  const [isTicketPending, setIsTicketPending] = useState(
    () => !!new URLSearchParams(window.location.search).get(SSO_TICKET_PARAM),
  );

  useEffect(() => {
    if (!isSignInLoaded || !isSessionLoaded || startedRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const ticket = params.get(SSO_TICKET_PARAM);
    if (!ticket) {
      return;
    }
    startedRef.current = true;

    // Strip the ticket from the URL before consuming it so the single-use
    // token never lands in history, bookmarks, or analytics page URLs.
    params.delete(SSO_TICKET_PARAM);
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );

    if (session) {
      // Already signed in on this origin; the ticket is redundant.
      setIsTicketPending(false);
      return;
    }

    if (!signIn) {
      console.warn('SSO ticket present but Clerk signIn is unavailable');
      setIsTicketPending(false);
      return;
    }

    signIn
      .create({ strategy: 'ticket', ticket })
      .then(result => {
        if (result.status === 'complete' && result.createdSessionId) {
          return setActive?.({ session: result.createdSessionId });
        }
        console.warn('SSO ticket sign-in did not complete:', result.status);
      })
      .catch(error => {
        // Fail open: the auth gate falls back to the studio /sign-in page.
        console.error('SSO ticket sign-in failed:', error);
      })
      .finally(() => {
        setIsTicketPending(false);
      });
  }, [isSignInLoaded, isSessionLoaded, session, signIn, setActive]);

  return { isTicketPending };
}
